<template>
  <gs-modal :value="true" class="gs-facebook-pixel-editor" size="lg"
    :title="pixelId ? 'Edit Facebook pixel' : 'Add new Facebook pixel'"
    subtitle="Connect a Facebook pixel for server-side purchase tracking."
    :close-on-backdrop="false" @close="close">
    <form class="gs-facebook-pixel-editor__form" @submit.prevent="save" novalidate>
      <p class="gs-facebook-pixel-editor__intro">This pixel will also be available in Vendor Settings and for your other products.</p>
      <label class="gs-facebook-pixel-editor__field">
        <span>Pixel name</span>
        <input v-model.trim="form.name" :disabled="saving" maxlength="80" autocomplete="off" placeholder="e.g. Main store pixel" @blur="$v.form.name.$touch()" :aria-invalid="$v.form.name.$error ? 'true' : 'false'" />
        <small>A name that helps you recognize this connection.</small>
        <span v-if="$v.form.name.$error" class="gs-facebook-pixel-editor__error">Enter a name between 3 and 80 characters.</span>
      </label>
      <label class="gs-facebook-pixel-editor__field">
        <span>Facebook pixel ID</span>
        <input v-model.trim="form.pixel_id" :disabled="saving" maxlength="80" autocomplete="off" placeholder="Your Facebook pixel ID" @blur="$v.form.pixel_id.$touch()" :aria-invalid="$v.form.pixel_id.$error ? 'true' : 'false'" />
        <span v-if="$v.form.pixel_id.$error" class="gs-facebook-pixel-editor__error">Enter your Facebook pixel ID (3–80 characters).</span>
      </label>
      <label class="gs-facebook-pixel-editor__field">
        <span>Conversions API access token</span>
        <textarea v-model.trim="form.access_token" :disabled="saving" autocomplete="off" spellcheck="false" rows="3" placeholder="Paste your access token" @blur="$v.form.access_token.$touch()" :aria-invalid="$v.form.access_token.$error ? 'true' : 'false'"></textarea>
        <span v-if="$v.form.access_token.$error" class="gs-facebook-pixel-editor__error">Enter your Conversions API access token.</span>
      </label>
      <a class="gs-facebook-pixel-editor__guide" href="https://developers.facebook.com/docs/marketing-api/conversions-api/get-started" target="_blank" rel="noopener noreferrer">Help finding your pixel ID and access token ↗</a>
      <p v-if="error" class="gs-facebook-pixel-editor__error" role="alert">{{ error }}</p>
      <footer class="gs-facebook-pixel-editor__actions">
        <gs-button type="button" variant="outline" :disabled="saving" @click="close">Cancel</gs-button>
        <gs-button type="submit" variant="primary" :disabled="saving">{{ saving ? 'Saving pixel…' : 'Save pixel' }}</gs-button>
      </footer>
    </form>
  </gs-modal>
</template>

<script>
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';

export default {
  name: 'FacebookPixelEditor',
  props: {
    pixel: { type: Object, default: () => ({}) },
    pixelId: { default: null },
  },
  data() {
    return {
      form: { name: this.pixel.name || '', pixel_id: this.pixel.pixel_id || '', access_token: this.pixel.access_token || '' },
      saving: false,
      error: '',
    };
  },
  validations: {
    form: {
      name: { required, minLength: minLength(3), maxLength: maxLength(80) },
      pixel_id: { required, minLength: minLength(3), maxLength: maxLength(80) },
      access_token: { required, minLength: minLength(3) },
    },
  },
  methods: {
    close() {
      if (!this.saving) this.$emit('close');
    },
    async save() {
      if (this.saving) return;
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.saving = true;
      this.error = '';
      try {
        const { data } = await axios.post(`${window.config.apiUrl}/save-conversion-settings`, {
          ...this.form, is_editing: Boolean(this.pixelId), id: this.pixelId,
        });
        if (!data || !data.id) throw new Error('Missing saved pixel');
        // Only selection metadata leaves this form; never copy access tokens
        // into product settings or the dropdown list.
        const saved = { id: data.id, name: data.name || this.form.name, pixel_id: data.pixel_id || this.form.pixel_id };
        this.form.access_token = '';
        this.$emit('saved', saved);
      } catch (_) {
        this.error = 'Unable to save this pixel. Your entries are still here; please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style>
.gs-facebook-pixel-editor .gs-modal__panel { width:min(640px,100%); }
.gs-facebook-pixel-editor .gs-modal__body { padding:24px; }
.gs-facebook-pixel-editor__form { display:flex; flex-direction:column; gap:18px; text-align:left; }
.gs-facebook-pixel-editor__intro { margin:0; color:var(--gs-muted-foreground); font-size:14px; line-height:1.5; }
.gs-facebook-pixel-editor__field { display:flex; flex-direction:column; gap:7px; color:var(--gs-foreground); font-size:14px; font-weight:600; }
.gs-facebook-pixel-editor__field input,
.gs-facebook-pixel-editor__field textarea { width:100%; min-width:0; padding:11px 12px; border:1px solid var(--gs-border); border-radius:8px; background:var(--gs-surface); color:var(--gs-foreground); font:inherit; font-weight:400; }
.gs-facebook-pixel-editor__field textarea { resize:vertical; min-height:86px; }
.gs-facebook-pixel-editor__field input:focus,
.gs-facebook-pixel-editor__field textarea:focus { outline:2px solid var(--gs-active); outline-offset:2px; }
.gs-facebook-pixel-editor__field small { color:var(--gs-muted-foreground); font-weight:400; }
.gs-facebook-pixel-editor__guide { font-size:13px; color:var(--gs-active); text-decoration:underline; }
.gs-facebook-pixel-editor__error { color:#b42318; font-size:13px; font-weight:500; margin:0; }
.gs-facebook-pixel-editor__actions { display:flex; justify-content:flex-end; gap:12px; padding-top:18px; border-top:1px solid var(--gs-border); }
@media(max-width:480px) {
  .gs-facebook-pixel-editor .gs-modal__body { padding:18px; }
  .gs-facebook-pixel-editor__actions > button { flex:1; }
}
</style>
